import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Loader2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { contractApi, type ReactivateStripeSubscriptionResult } from '@/features/contracts/api/contractApi';

interface ReactivateStripeSubscriptionModalProps {
  contractId: number;
  subscriptionUuid: string;
  contractLabel: string;
  contractDuration: number;
  isOpen: boolean;
  onClose: () => void;
}

function formatIban(last4: string | null, country: string | null): string {
  if (!last4) return '—';
  const prefix = country ? `${country}** ` : '';
  return `${prefix}**** **** **** ${last4}`;
}

function formatEuros(amount: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatDateFromUnixSec(unixSec: number | null): string {
  if (!unixSec) return 'immédiat (à la création)';
  return new Date(unixSec * 1000).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function ReactivateStripeSubscriptionModal({
  contractId,
  subscriptionUuid,
  contractLabel,
  contractDuration,
  isOpen,
  onClose,
}: ReactivateStripeSubscriptionModalProps) {
  const queryClient = useQueryClient();
  const [iterationsOverride, setIterationsOverride] = useState<string>('');
  const [preview, setPreview] = useState<ReactivateStripeSubscriptionResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dryRunMutation = useMutation({
    mutationFn: () =>
      contractApi.reactivateStripeSubscription(subscriptionUuid, {
        dryRun: true,
        remainingIterations: iterationsOverride ? parseInt(iterationsOverride, 10) : undefined,
      }),
    onSuccess: (data) => {
      setPreview(data);
      setErrorMessage(null);
      if (!iterationsOverride) {
        setIterationsOverride(String(data.iterations));
      }
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Erreur lors de la simulation';
      setErrorMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPreview(null);
    },
  });

  const executeMutation = useMutation({
    mutationFn: () =>
      contractApi.reactivateStripeSubscription(subscriptionUuid, {
        dryRun: false,
        remainingIterations: iterationsOverride ? parseInt(iterationsOverride, 10) : undefined,
      }),
    onSuccess: (data) => {
      if (data.status === 'no_op') {
        toast.info('Le contrat est déjà actif, aucune action effectuée');
      } else {
        toast.success(`Souscription réactivée (${data.newSubscriptionId})`);
      }
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['payment-details', contractId] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      // Reset state + close direct sans passer par handleClose() : la closure
      // de handleClose capture isLoading=true du render précédent et
      // court-circuiterait la fermeture.
      setPreview(null);
      setIterationsOverride('');
      setErrorMessage(null);
      onClose();
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.message || err?.message || "Erreur lors de l'exécution";
      const recovery = data?.recovery
        ? `\nDonnées de recovery manuel : ${JSON.stringify(data.recovery)}`
        : '';
      setErrorMessage((typeof msg === 'string' ? msg : JSON.stringify(msg)) + recovery);
    },
  });

  const handleClose = () => {
    if (dryRunMutation.isLoading || executeMutation.isLoading) return;
    setPreview(null);
    setIterationsOverride('');
    setErrorMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  const isBusy = dryRunMutation.isLoading || executeMutation.isLoading;
  const hasValidPreview = preview && preview.status === 'dry_run';

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-2">
            <RefreshCw className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
              Réactiver la souscription Stripe
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isBusy}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-p-5 tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-600 tw-m-0">
            Contrat <strong>{contractLabel}</strong> (durée totale : {contractDuration} mois).
            Une nouvelle souscription Stripe sera créée en réutilisant le mandat SEPA existant
            du bénéficiaire — aucune action n'est requise de sa part.
          </p>

          {/* Étape 1 : simulation */}
          {!hasValidPreview && (
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-md tw-p-4">
              <p className="tw-text-sm tw-text-blue-800 tw-m-0 tw-mb-3">
                Lancez d'abord une simulation pour vérifier les informations qui seront utilisées.
              </p>
              <button
                onClick={() => dryRunMutation.mutate()}
                disabled={isBusy}
                className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
              >
                {dryRunMutation.isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
                Lancer la simulation
              </button>
            </div>
          )}

          {/* Étape 2 : récap dry-run + confirmation */}
          {hasValidPreview && preview && (
            <>
              <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md tw-p-4 tw-space-y-3">
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-gray-700">
                  <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-green-600" />
                  Simulation réussie — vérifiez les informations avant de confirmer
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2 tw-text-sm">
                  <div>
                    <span className="tw-text-gray-500">Ancienne souscription</span>
                    <div className="tw-font-mono tw-text-xs tw-text-gray-900 tw-break-all">
                      {preview.oldSubscriptionId}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500">
                      statut Stripe : {preview.oldSubStatus}
                    </div>
                  </div>
                  <div>
                    <span className="tw-text-gray-500">Mensualités déjà payées</span>
                    <div className="tw-font-semibold tw-text-gray-900">
                      {preview.paidCount} / {contractDuration}
                    </div>
                  </div>

                  <div>
                    <span className="tw-text-gray-500">Prix mensuel</span>
                    <div className="tw-font-semibold tw-text-gray-900">
                      {formatEuros(preview.monthlyPriceClient)}
                    </div>
                  </div>
                  <div>
                    <span className="tw-text-gray-500">Frais de service</span>
                    <div className="tw-text-gray-900">
                      {preview.feePercentage}%
                    </div>
                  </div>

                  <div className="sm:tw-col-span-2 tw-border-t tw-border-gray-200 tw-pt-3">
                    <span className="tw-text-gray-500">Mandat SEPA réutilisé</span>
                    <div className="tw-font-mono tw-text-sm tw-text-gray-900">
                      {formatIban(preview.paymentMethod.sepaLast4, preview.paymentMethod.country)}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500">
                      Banque : {preview.paymentMethod.bankCode || '—'} ·
                      Customer Stripe : <span className="tw-font-mono">{preview.customerId}</span>
                    </div>
                  </div>
                </div>

                {/* Continuité du calendrier : catch-up + 1er prélèvement nouvelle sub */}
                <div className="tw-border-t tw-border-gray-200 tw-pt-3 tw-space-y-2">
                  {preview.missedCount > 0 && (
                    <div className="tw-rounded-md tw-bg-orange-50 tw-border tw-border-orange-200 tw-p-3">
                      <div className="tw-text-sm tw-font-semibold tw-text-orange-900">
                        Mensualité{preview.missedCount > 1 ? 's' : ''} manquée{preview.missedCount > 1 ? 's' : ''} à rattraper
                      </div>
                      <div className="tw-text-xs tw-text-orange-800 tw-mt-1">
                        <strong>{preview.missedCount}</strong> mensualité{preview.missedCount > 1 ? 's' : ''} sera prélevée immédiatement
                        sur le mandat SEPA, montant : <strong>{formatEuros(preview.catchUpAmountCents / 100)}</strong>.
                        Le calendrier de la nouvelle souscription respectera ensuite le jour d'origine.
                      </div>
                    </div>
                  )}
                  <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-text-sm">
                    <div>
                      <span className="tw-text-gray-500">1er prélèvement nouvelle sub</span>
                      <div className="tw-font-semibold tw-text-gray-900">
                        {formatDateFromUnixSec(preview.firstSubChargeAtSec)}
                      </div>
                    </div>
                    {preview.missedCount === 0 && (
                      <div className="tw-text-xs tw-text-gray-500 tw-self-end">
                        Aucune mensualité manquée — le calendrier d'origine est conservé.
                      </div>
                    )}
                  </div>
                </div>

                {/* Itérations ajustables */}
                <div className="tw-border-t tw-border-gray-200 tw-pt-3">
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Nombre de mensualités à créer
                  </label>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="number"
                      min={1}
                      max={contractDuration}
                      value={iterationsOverride}
                      onChange={(e) => setIterationsOverride(e.target.value)}
                      className="tw-w-24 tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm"
                    />
                    <span className="tw-text-xs tw-text-gray-500">
                      Recommandé : {Math.max(0, contractDuration - preview.paidCount - preview.missedCount)}
                      &nbsp;({contractDuration} - {preview.paidCount} payées
                      {preview.missedCount > 0 ? ` - ${preview.missedCount} catch-up` : ''})
                    </span>
                    <button
                      onClick={() => dryRunMutation.mutate()}
                      disabled={isBusy}
                      className="tw-ml-auto tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-text-blue-700 tw-border tw-border-blue-300 hover:tw-bg-blue-50 tw-transition-colors disabled:tw-opacity-50"
                    >
                      {dryRunMutation.isLoading && <Loader2 className="tw-w-3 tw-h-3 tw-animate-spin" />}
                      Re-simuler
                    </button>
                  </div>
                </div>
              </div>

              <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md tw-p-3 tw-flex tw-gap-2">
                <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                <p className="tw-text-xs tw-text-amber-800 tw-m-0">
                  Cette action va créer une nouvelle souscription Stripe et réactiver le contrat
                  (statut → actif). Aucun email ne sera envoyé.
                </p>
              </div>
            </>
          )}

          {/* Erreur */}
          {errorMessage && (
            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-3 tw-text-xs tw-text-red-800 tw-whitespace-pre-wrap">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200">
          <button
            onClick={handleClose}
            disabled={isBusy}
            className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
          >
            Annuler
          </button>
          {hasValidPreview && (
            <button
              onClick={() => executeMutation.mutate()}
              disabled={isBusy}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-dark tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {executeMutation.isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
              Confirmer la réactivation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
